import { addToGroupsAdv, createNewGroupAdv, removeFromGroupsAdv } from '@/apis/groups';
import { updatePathwaysAdv } from '@/apis/pathways';
import { setSupporterStatusAdv } from '@/apis/supporterstatuses';
import { addToTagsAdv, removeFromTagsAdv } from '@/apis/tags';
import { updateTasksAdv } from '@/apis/tasks';
import { TASK_CHOICE_MAPPING, TASK_KIND_MAPPING } from '@/types';
import { ExtendedSearchTreeNode } from '@/types/searchTreeTypes';
import { logger } from '@/util';
import { domainCodeAtom, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useFeatureToggle } from '@/featureToggles/FeatureTogglesContext';

export { useOtherActionsPanelList as useActionList } from '@components/OtherActionsPanelContent/shared/useOtherActionsPanelList';
export { useDeleteContactsFromList } from '@components/OtherActionsPanelContent/shared/useDeleteContactsFromList';

export type ApplyOtherActionsMutationVars = {
  tree: ExtendedSearchTreeNode;
  addToGroupIds?: number[];
  removeFromGroupIds?: number[];
  createGroupTitles?: string[];
  addToTagIds?: number[];
  removeFromTagIds?: number[];
  updateSupporterStatus?: 'P' | 'C' | 'X';
  updateTasks?: {
    electionId: number;
    taskKindType: keyof typeof TASK_KIND_MAPPING;
    levelOfInterest: keyof typeof TASK_CHOICE_MAPPING;
    notes?: string;
  };
  updatePathway?: {
    stepId: number;
    optionId: 1 | 2 | 3;
  };
};

export const useApplyOtherActions = () => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const { enqueueSuccessNotification } = useSaladBar();
  const printTime = useFeatureToggle('Print Advanced Search Time');
  const useAdvancedSearchII = useFeatureToggle('Use Advanced Search II');
  return useMutation({
    mutationFn: async ({
      tree,
      addToGroupIds,
      removeFromGroupIds,
      createGroupTitles,
      addToTagIds,
      removeFromTagIds,
      updateSupporterStatus,
      updateTasks,
      updatePathway,
    }: ApplyOtherActionsMutationVars) => {
      const token = await getAccessTokenSilently();
      tree.printTime = printTime;
      tree.useAdvancedSearchII = useAdvancedSearchII;
      logger.info('Starting Other Action for Advanced Search');
      const t0 = performance.now();

      if (addToGroupIds) {
        await addToGroupsAdv(token, { tree, domainCode, addToGroupIds });
        enqueueSuccessNotification('Contacts added to group(s)');
      }

      if (removeFromGroupIds) {
        await removeFromGroupsAdv(token, { tree, domainCode, removeFromGroupIds });
        enqueueSuccessNotification('Contacts removed from group(s)');
      }

      if (createGroupTitles) {
        await createNewGroupAdv(token, { tree, domainCode, titles: createGroupTitles });
        enqueueSuccessNotification('Contacts added to new group(s)');
      }

      if (addToTagIds) {
        await addToTagsAdv(token, { tree, domainCode, addToTagIds });
        enqueueSuccessNotification('Tag(s) added to contacts');
      }

      if (removeFromTagIds) {
        await removeFromTagsAdv(token, { tree, domainCode, removeFromTagIds });
        enqueueSuccessNotification('Tag(s) removed from contacts');
      }

      if (updateSupporterStatus) {
        await setSupporterStatusAdv(token, {
          tree,
          domainCode,
          supporterStatus: updateSupporterStatus,
        });
        enqueueSuccessNotification('Rocket status updated for contact(s)');
      }

      if (updateTasks) {
        await updateTasksAdv(token, { tree, domainCode, ...updateTasks });
        enqueueSuccessNotification('Task status updated for contacts');
      }

      if (updatePathway) {
        await updatePathwaysAdv(token, {
          tree,
          domainCode,
          updatePathwayStepId: updatePathway.stepId,
          updatePathwayOptionId: updatePathway.optionId,
        });
        enqueueSuccessNotification('Pathway updated for contact(s)');
      }

      const timeMs = performance.now() - t0;
      logger.info(`Finished Other Action for Advanced Search [${Math.round(timeMs)}ms]`);
    },
  });
};
